import type { Operator } from "./index.js"

// Set membership — value is in a list.

export const isOneOf: Operator = {
  key: "isOneOf",
  label: "is one of",
  icon: "list-check",
  value: "array",
  fn: (a, b) => Array.isArray(b) && b.includes(a),
}

export const isNotOneOf: Operator = {
  key: "isNotOneOf",
  label: "is not one of",
  icon: "list",
  value: "array",
  // Null-excluding, matching `col != ALL(...)`. See notEquals in equality.ts.
  fn: (a, b) => a != null && (!Array.isArray(b) || !b.includes(a)),
}

// Multi-value relationship operators (tags, links, parents, reverse_links). Their
// SQL depends on the path, so Postgres's filter_condition_sql builds it per
// relationship.

export const hasAny: Operator = {
  key: "hasAny",
  label: "has any of",
  icon: "circle-check",
  value: "array",
  fn: (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return false
    const aIds = a.map((tag) => (tag as { id?: unknown })?.id ?? tag)
    const bIds = b.map((tag) => (tag as { id?: unknown })?.id ?? tag)
    return bIds.some((tagId) => aIds.includes(tagId))
  },
}

export const hasAll: Operator = {
  key: "hasAll",
  label: "has all of",
  icon: "check-double",
  value: "array",
  fn: (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return false
    const aIds = a.map((tag) => (tag as { id?: unknown })?.id ?? tag)
    const bIds = b.map((tag) => (tag as { id?: unknown })?.id ?? tag)
    return bIds.every((tagId) => aIds.includes(tagId))
  },
}

// The set negation of hasAny. Unlike the column negations (notEquals, isNotOneOf, …),
// it keeps a record with no related rows: its SQL is a NOT EXISTS over the
// relationship, not `col != …`, so an empty set — like a missing one — has none.
export const hasNone: Operator = {
  key: "hasNone",
  label: "has none of",
  icon: "circle-xmark",
  value: "array",
  fn: (a, b) => !hasAny.fn(a, b),
}
